import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { MetronLogo } from "@/components/MetronLogo";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import {
  LayoutDashboard, ClipboardCheck, LogOut, Menu, X, Settings, BarChart3, Share2,
} from "lucide-react";
import { useState } from "react";
import { shareMetron } from "@/lib/share";

const BG = "https://static.prod-images.emergentagent.com/jobs/963b50f1-f77c-4e83-99db-abb6c6ad95ea/images/3cad815a8389e7905812a3a7d9147972f44378e5ac89bef2336b714c533d18ad.jpeg";

const NAV = [
  { to: "/", label: "Início", icon: LayoutDashboard, end: true },
  { to: "/avaliar", label: "Avaliar", icon: ClipboardCheck },
  { to: "/resultados", label: "Resultados", icon: BarChart3 },
  { to: "/configuracoes", label: "Configurações", icon: Settings },
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [confirmOut, setConfirmOut] = useState(false);

  const doLogout = () => {
    setConfirmOut(false);
    logout();
    navigate("/login");
  };

  const roleLabel = user?.role === "admin" ? "Administrador" : "Instrutor";
  const grau = user?.grau_nome && user.grau_nome !== "Não se aplica" ? String(user.grau_nome).trim() : "";
  const nomeExib = (user?.nome_guerra?.trim() || user?.name || "").trim();
  const startsWithRole = nomeExib.toLowerCase().startsWith(roleLabel.toLowerCase());
  const identidade = [grau, nomeExib].filter(Boolean).join(" ").trim();
  const saudacao = `Olá, ${[startsWithRole ? "" : roleLabel, identidade].filter(Boolean).join(" ")}`.replace(/\s+/g, " ").trim();

  const SidebarContent = () => (
    <>
      <div className="flex items-center gap-3 px-5 py-5 border-b border-border">
        <MetronLogo className="h-9 w-9" />
        <div>
          <div translate="no" className="notranslate font-heading text-2xl font-extrabold tracking-tight leading-none text-foreground">MÉTRON</div>
          <div className="text-[10px] uppercase tracking-widest font-mono leading-tight mt-0.5" style={{ color: "#D3A46F" }}>Plataforma Modular de Avaliação e Gestão de Competências</div>
        </div>
      </div>
      <div data-testid="saudacao-usuario" className="px-5 py-2 text-xs text-muted-foreground border-b border-border truncate">{saudacao}</div>
      <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1">
        {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${n.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                isActive
                  ? "bg-primary text-primary-foreground shadow-[0_6px_18px_rgba(41,149,154,0.35)]"
                  : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
              }`
            }
          >
            <n.icon style={{ width: 18, height: 18 }} />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="shrink-0 px-3 py-4 border-t border-border" style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
        <div className="px-3 py-2 mb-2">
          <div className="text-sm font-semibold truncate">{identidade || user?.name}</div>
          <div className="text-[11px] uppercase tracking-wide font-mono" style={{ color: "#D3A46F" }}>
            {roleLabel}
          </div>
        </div>
        <button
          onClick={() => { setOpen(false); shareMetron(); }}
          data-testid="nav-compartilhar"
          className="flex w-full items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-muted-foreground hover:bg-secondary/60 hover:text-foreground transition-colors mb-1"
        >
          <Share2 style={{ width: 18, height: 18 }} /> Compartilhar MÉTRON
        </button>
        <button
          onClick={() => setConfirmOut(true)}
          data-testid="logout-button"
          className="flex w-full items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-muted-foreground hover:bg-destructive/15 hover:text-red-300 transition-colors"
        >
          <LogOut style={{ width: 18, height: 18 }} /> Sair da conta
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex">
      {/* Fundo global discreto */}
      <div className="fixed inset-0 -z-10 bg-background" />
      <div className="fixed inset-0 -z-10 bg-cover bg-center opacity-[0.06]" style={{ backgroundImage: `url(${BG})` }} />

      <aside className="hidden md:flex w-64 flex-col fixed inset-y-0 border-r border-border bg-card/90 backdrop-blur-md">
        <SidebarContent />
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between px-4 h-14 border-b border-border bg-card/90 backdrop-blur-md">
        <div className="flex items-center gap-2">
          <MetronLogo className="h-7 w-7" />
          <span translate="no" className="notranslate font-heading text-xl font-extrabold">MÉTRON</span>
        </div>
        <button onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" className="p-2">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="w-64 flex flex-col border-r border-border bg-card backdrop-blur-md">
            <SidebarContent />
          </div>
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)} />
        </div>
      )}

      {confirmOut && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4" onClick={() => setConfirmOut(false)}>
          <div data-testid="logout-confirm" className="w-full max-w-sm rounded-2xl border border-border p-6 bg-popover" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-bold">Deseja sair da sua conta?</h3>
            <p className="text-sm text-muted-foreground mt-1">Sua sessão será encerrada.</p>
            <div className="flex gap-2 justify-end mt-5">
              <button data-testid="logout-cancel" onClick={() => setConfirmOut(false)} className="px-4 py-2 rounded-xl text-sm border border-border hover:bg-secondary/60">Cancelar</button>
              <button data-testid="logout-confirm-btn" onClick={doLogout} className="px-4 py-2 rounded-xl text-sm bg-red-500/90 text-white hover:bg-red-500">Sair</button>
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 md:ml-64 pt-14 md:pt-0 min-w-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 md:py-8">
          <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
